import * as React from 'react'
import { useRouter } from 'next/router'
import { GetStaticProps } from 'next'
import apolloClient from '@/lib/utils/apollo-client'
import getCraftSiteHandle from '@/lib/utils/get-craft-site-handle'
import Seo from '@/components/Seo'
import dynamic from 'next/dynamic'
import AppHeader from '@/components/AppHeader'
import AppFooter from '@/components/AppFooter'
import getPreviewToken from '@/lib/utils/get-preview-token'
import Archive from '@/components/Archive'

import { QUERY_SITE_WIDE } from '@/gql/shared-queries'
import { QUERY_ARTICLES_ARCHIVE_PAGE } from '@/gql/shared-queries'

import { SiteWideQuery_globalSet } from '@/gql/types/SiteWideQuery'
import {
    StandardArticlesQuery_entries_articles_article_Entry,
    StandardArticlesQuery_entry_articlesArchive_articlesArchive_Entry,
} from '@/gql/types/StandardArticlesQuery'
import { FragmentImage } from '@/gql/types/FragmentImage'

const componentList: { [key: string]: React.ComponentType<any> } = {
    secondaryBanner: dynamic(() => import(`@/components/Blocks/Modules/SecondaryBanner`)),
}

type ArticleIndexPageType = {
    entry: StandardArticlesQuery_entry_articlesArchive_articlesArchive_Entry
    entries: StandardArticlesQuery_entries_articles_article_Entry[]
    entryCount: Number
    siteWide: SiteWideQuery_globalSet
    itemsPerPage: Number
    currentPage: Number
}

const ArticleIndexPage: React.FunctionComponent<ArticleIndexPageType> = ({
    entry,
    entries,
    entryCount,
    siteWide,
    itemsPerPage,
    currentPage,
}) => {
    const router = useRouter()
    const metaImage = entry.metaImage![0] as FragmentImage
    // TODO: Edit TypeScript type StandardArticlesQuery_entry_articlesArchive_articlesArchive_Entry_modules
    const components = entry.modules!.map((module: any) => {
        return { name: componentList[module!.typeHandle!], data: module }
    })

    return (
        <>
            <Seo
                siteWide={siteWide}
                title={entry.metaTitle!}
                description={entry.metaDescription!}
                image={metaImage}
                canonical={`${router.asPath}`}
            />
            <AppHeader mode="fixed" siteWide={siteWide} entry={entry} />
            {components.map((Component, index) => {
                return <Component.name key={index} data={Component.data} />
            })}
            <Archive
                heading="News &amp; Stories"
                subheading="Our community"
                entries={entries}
                entryCount={entryCount}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
            />
            <AppFooter siteWide={siteWide} bgColor="bg-black" />
        </>
    )
}

export const getStaticProps: GetStaticProps = async ({ params, locale, preview, previewData }) => {
    const previewToken = getPreviewToken(previewData, preview)

    const itemsPerPage = 12
    let currentPage: number = 1
    if (params && params.page && !isNaN(params.page as unknown as number)) {
        currentPage = Number(params.page as unknown as number)
    }
    const offset = (currentPage - 1) * itemsPerPage

    const { data } = await apolloClient(previewToken).query({
        query: QUERY_ARTICLES_ARCHIVE_PAGE,
        variables: {
            site: getCraftSiteHandle(locale),
            offset: offset,
            itemsPerPage: itemsPerPage,
        },
    })

    const { data: siteWide } = await apolloClient(previewToken).query({
        query: QUERY_SITE_WIDE,
    })

    if (data.entry === null || !data.entries.length) {
        return {
            notFound: true,
            revalidate: 1,
        }
    }

    return {
        props: {
            entry: data.entry,
            entries: data.entries as StandardArticlesQuery_entries_articles_article_Entry[],
            entryCount: data.entryCount,
            siteWide: siteWide.globalSet as SiteWideQuery_globalSet,
            currentPage: currentPage,
            itemsPerPage: itemsPerPage,
        },
        revalidate: 1,
    }
}

export default ArticleIndexPage
